import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIcalCalendarSettings } from '@/features/integrations/api/ical-calendar/ical-calendar.hook';
import { Copy, MoreHorizontal, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/** Adds sharing to the existing settings screen. Google users retain their old flow. */
export function IcalCalendarSettings() {
  const [person, setPerson] = useState<string>();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [kind, setKind] = useState<'ical' | 'treatwell'>('ical');
  const [showAddSource, setShowAddSource] = useState(false);
  const [showFeedUrl, setShowFeedUrl] = useState(false);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const {
    data,
    isPending,
    isError,
    refetch,
    busy,
    addingSource,
    enable,
    disable,
    addSource,
    removeSource,
    refreshSource,
  } = useIcalCalendarSettings(person);
  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setShowFeedUrl(false);
      toast.success('Calendar link copied');
    } catch {
      setShowFeedUrl(true);
      toast.error('Could not copy. Select the link and copy it.');
    }
  };

  return (
    <section className="space-y-6" aria-label="iCal settings">
      {isPending ? (
        <p role="status" className="text-sm text-muted-foreground">
          Loading calendar settings…
        </p>
      ) : isError ? (
        <div className="space-y-2">
          <p role="alert">Calendar settings could not be loaded.</p>
          <Button variant="outline" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        data && (
          <>
            {data.practitioners.length > 1 && (
              <Select
                value={data.userId}
                onValueChange={(value) => {
                  setPerson(value);
                  setShowAddSource(false);
                  setShowFeedUrl(false);
                  setConfirmDisable(false);
                  setName('');
                  setUrl('');
                  setKind('ical');
                }}
                disabled={busy}
              >
                <SelectTrigger
                  id="ical-practitioner"
                  aria-label="Team member"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {data.practitioners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {data.googleLinked ? (
              <p className="text-sm text-muted-foreground">
                Google Calendar sync is linked for this team member. Disconnect
                it before using iCal.
              </p>
            ) : !data.enabled ? (
              <Button disabled={busy} onClick={enable}>
                {busy ? 'Enabling…' : 'Enable iCal sharing'}
              </Button>
            ) : (
              <>
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-medium">Share bookings</h3>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="iCal options"
                          disabled={busy}
                        >
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          variant="destructive"
                          onSelect={() => {
                            setShowAddSource(false);
                            setConfirmDisable(true);
                          }}
                        >
                          Turn off iCal sharing
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Subscribe in your calendar. Includes client and booking
                    details, so keep this link private.
                  </p>
                  <Button
                    className="w-full sm:w-auto"
                    variant={
                      showAddSource || confirmDisable ? 'outline' : 'default'
                    }
                    disabled={!data.feedUrl}
                    onClick={() => data.feedUrl && void copy(data.feedUrl)}
                  >
                    <Copy className="size-4" />
                    Copy calendar link
                  </Button>
                  {showFeedUrl && (
                    <Input
                      aria-label="Private calendar link"
                      readOnly
                      value={data.feedUrl || ''}
                      onFocus={(event) => event.target.select()}
                    />
                  )}
                </div>
                <div className="space-y-3">
                  <h3 className="font-medium">Block time booked elsewhere</h3>
                  {data.sources.map((source) => (
                    <div key={source.id} className="space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 space-y-1">
                          <p className="font-medium">{source.name}</p>
                          {(source.lastError || !source.lastSyncAt) && (
                            <p className="text-sm text-muted-foreground">
                              {source.lastError ||
                                (source.kind === 'treatwell'
                                  ? 'Waiting for a Treatwell notification'
                                  : 'Waiting for the first import')}
                            </p>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={`Options for ${source.name}`}
                              disabled={busy}
                            >
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {source.kind === 'ical' && (
                              <DropdownMenuItem
                                onSelect={() => refreshSource(source.id)}
                              >
                                <RefreshCw className="size-4" />
                                Refresh calendar
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => removeSource(source.id)}
                            >
                              <Trash2 className="size-4" />
                              Remove calendar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {source.email && (
                        <details className="text-sm">
                          <summary className="cursor-pointer text-muted-foreground">
                            Setup instructions
                          </summary>
                          <Field className="mt-3">
                            <FieldLabel htmlFor={`source-${source.id}`}>
                              Treatwell notification address
                            </FieldLabel>
                            <Input
                              id={`source-${source.id}`}
                              readOnly
                              value={source.email}
                              onFocus={(event) => event.target.select()}
                            />
                            <p className="text-sm text-muted-foreground">
                              Add this as the secondary notification email in
                              Treatwell. New bookings, changes and cancellations
                              update this team member’s blocks.
                            </p>
                          </Field>
                        </details>
                      )}
                    </div>
                  ))}
                  {showAddSource ? (
                    <form
                      className="space-y-3"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void addSource({ name, kind, url })
                          .then(() => {
                            setName('');
                            setUrl('');
                            setShowAddSource(false);
                          })
                          .catch(() => {});
                      }}
                    >
                      {data.treatwellAvailable && (
                        <Field>
                          <FieldLabel htmlFor="ical-source-kind">
                            Source
                          </FieldLabel>
                          <Select
                            value={kind}
                            onValueChange={(value) =>
                              setKind(value as 'ical' | 'treatwell')
                            }
                          >
                            <SelectTrigger
                              id="ical-source-kind"
                              className="w-full sm:max-w-sm"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ical">
                                Calendar subscription
                              </SelectItem>
                              <SelectItem value="treatwell">
                                Treatwell notifications
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                      )}
                      {kind === 'ical' && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          <Field>
                            <FieldLabel htmlFor="ical-source-name">
                              Calendar name
                            </FieldLabel>
                            <Input
                              id="ical-source-name"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                              maxLength={100}
                              required
                              placeholder="Personal calendar"
                            />
                          </Field>
                          <Field>
                            <FieldLabel htmlFor="ical-source-url">
                              Subscription link
                            </FieldLabel>
                            <Input
                              id="ical-source-url"
                              value={url}
                              onChange={(e) => setUrl(e.target.value)}
                              required
                              placeholder="https://…"
                              inputMode="url"
                            />
                          </Field>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <Button
                          type="submit"
                          disabled={busy || data.sources.length >= 10}
                        >
                          {addingSource
                            ? 'Adding…'
                            : kind === 'treatwell'
                              ? 'Link Treatwell notifications'
                              : 'Add calendar'}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => setShowAddSource(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <Button
                      variant="outline"
                      disabled={busy || data.sources.length >= 10}
                      onClick={() => {
                        setConfirmDisable(false);
                        setShowAddSource(true);
                      }}
                    >
                      <Plus className="size-4" />
                      Add calendar
                    </Button>
                  )}
                </div>
                {confirmDisable && (
                  <div className="space-y-3 border-t pt-4">
                    <p className="text-sm">
                      Turning off iCal revokes this link and removes its
                      imported blocks. Your Borrah bookings stay in place.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="destructive"
                        disabled={busy}
                        onClick={() => {
                          disable();
                          setConfirmDisable(false);
                        }}
                      >
                        Turn off iCal sharing
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() => setConfirmDisable(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )
      )}
    </section>
  );
}
